import { Component, ElementRef, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef, signal } from "@angular/core";
import { Router } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import { MAT_DATE_FORMATS } from "@angular/material/core";
import { UntypedFormControl, FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { DateTime } from "luxon";
import { Pair } from "../obj/Pair";
import { AbstractBuyerComponent } from "./abstract/buyer.component";
import { EntityBase } from "../entity/EntityBase";
import { AuthService } from "../service/auth.service";
import { AnalyticsService } from "../service/analytics.service";
import { GeneralService } from "../service/general.service";
import { ValidationService } from "../service/validation.service";
import { State } from "../entity/State";
import { Account } from "../entity/Account";
import { Utility } from "../entity/Utility";
import { ExistingContract } from "../entity/ExistingContract";
import { User } from "../entity/User";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ConfirmationDialog } from "./incs/confirmation-dialog.component";
import { AccountExampleDialog } from "./incs/account-example-dialog.component";
import { LoadZone } from "../entity/LoadZone";
import { RateClass } from "../entity/RateClass";
import { AccountConsumption } from "../obj/AccountConsumption";
import { Company } from "../entity/Company";
import { ContractStatus } from "../obj/ContractStatus";
import { AccountNumberIssueDialog } from "./incs/account-number-issue-dialog.component";
import { LoaderService } from "../service/loader.service";
import { environment } from "../../environments/environment";
import { HeaderComponent } from "./incs/header.component";
import { CurrencyPipe, DatePipe } from "@angular/common";
import { NgxMaskDirective } from "ngx-mask";
import { MatDatepickerInput, MatDatepicker } from "@angular/material/datepicker";
import { OneClickDirective } from "./helpers/one-click.component";
import { firstValueFrom } from "rxjs";

export const MY_FORMATS = {
    parse: {
        dateInput: "MM/dd/yyyy",
    },
    display: {
        dateInput: "MM/dd/yyyy",
        monthYearLabel: "MMM yyyy",
        dateA11yLabel: "LL",
        monthYearA11yLabel: "MMMM yyyy",
    },
};

@Component({
    selector: "kilowant-account-details",
    templateUrl: "../../html/main-account-details.html",
    providers: [
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS }
    ],
    imports: [HeaderComponent, FormsModule, NgxMaskDirective, ReactiveFormsModule, MatDatepickerInput, MatDatepicker, OneClickDirective, CurrencyPipe, DatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountDetailsComponent extends AbstractBuyerComponent {
    @ViewChild('serviceName') serviceName: ElementRef;
    welcomeText: boolean = false;
    setWelcomeForMyKilowant: boolean = false;
    account: Account = new Account();
    consumptions: AccountConsumption[] = [];
    utilities: Utility[] = [];
    rateClasses: RateClass[] = [];
    states: State[] = [];
    cities: string[] = [];
    currentAccountNumber: FormControl = new FormControl("");
    loadZones: LoadZone[] = [];
    utilityId: number | null = null;
    globalStart: DateTime;
    globalEnd: DateTime;
    config: Pair[] = null!;
    imageMode: number = 0;
    selectCompany: boolean = false;
    addCompanyName: boolean = false;

    dialogRef: MatDialogRef<ConfirmationDialog> = null!;
    existingContractId: number | null = null;
    existingContracts: ExistingContract[] = [];
    confirmButtonActive: boolean = true;

    utilityRate: number | null = null;
    communityRate: number | null = null;
    setStartingContract: number | null = null;
    existingContract: ExistingContract = new ExistingContract();

    termFrom = new UntypedFormControl();
    termTo = new UntypedFormControl();

    step: number = 1;

    fileToUpload: File | null = null;
    billsToUpload: File[] = [];
    deleteTaxExemptCertificateDocument: boolean = false;
    defaultShowAccountInfo: boolean = true;

    toBeUsedUser: User;
    preRegisteredAccountUpdateMode: boolean = false;

    loading: boolean = false;
    progress: boolean = false;
    progressWidth: number = 0;

    userCompanies: Company[];
    isCommercial = signal<boolean>(false);

    disableSelectorOfUtilityData: boolean = false;
    newCompanyRegistration = signal<boolean>(false);
    taxExemptAllowToAdd = signal<boolean>(true);
    taxExempt: boolean = false;

    constructor(
        router: Router,
        route: ActivatedRoute,
        authService: AuthService,
        protected loaderService: LoaderService,
        analyticsService: AnalyticsService,
        generalService: GeneralService,
        public validationService: ValidationService,
        dialog: MatDialog
    ) {
        super(router, route, dialog, authService, analyticsService, generalService);
    }

    ngOnInit(): void {
        this.analyticsService.trackEvent("Account Details Page", "User Opens Account Details Page", "OPEN_PAGE");
        this.initPromises.push(
            this.generalService.getUtilities(this.generalService.getDefaultFuelType(), true, false, null).then((utilities) => {
                this.utilities = utilities;
                this.account.fuelType = this.generalService.getDefaultFuelType();
                let u = this.generalService.getDefaultUtilityFromList(this.utilities);
                this.account.utility = u;
                if (u != null) {
                    this.utilityId = u.id;
                }
                this.refreshView();
            })
        );
        super.ngOnInit();
    }

    pageStart(): void {
        if (this.router.url === "/account/onboarding") {
            this.welcomeText = true;
            this.setWelcomeForMyKilowant = true;
        }
        var preRegisterInit = false;
        if (this.router.url === "/account/init") {
            preRegisterInit = true;
        }
        if (this.user.isAdvisor) {
            let client = this.generalService.getStoredClient();
            if (client != null) {
                this.toBeUsedUser = client;
            } else {
                this.toBeUsedUser = this.user;
            }
        } else {
            this.toBeUsedUser = this.user;
        }
        this.globalStart = this.currentDate.minus({ years: 5 });
        this.globalEnd = this.currentDate.plus({ years: 5 });
        this.account.user = this.user;
        this.account.billImages = [];
        this.consumptions = [];
        if (preRegisterInit) {
            this.generalService.getAccounts(true).then((accounts) => {
                this.checkAutoGeneratedAccount(accounts[0]);
                this.initCompanyRelatedData();
                this.refreshView();
            });
        }
        this.reloadSelects();
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    fillAccountUsingUserData(): void {
        this.account.serviceName = this.toBeUsedUser.firstName + " " + this.toBeUsedUser.lastName;
        this.account.addressLine1 = this.toBeUsedUser.addressLine1;
        this.account.addressLine2 = this.toBeUsedUser.addressLine2;
        this.account.state = this.toBeUsedUser.state;
        this.account.city = this.toBeUsedUser.city;
        this.account.zip = this.toBeUsedUser.zip;
    }

    goToStart(): void {
        this.clearErrors();
        this.step = 1;
        this.refreshView();
    }

    nextStep(): void {
        this.clearErrors();
        if (this.step == 1) {
            if (this.imageMode == 0 || this.imageMode == 2) {
                this.parseBill();
                return;
            }
            if (!this.account.rateClass?.isCommercial) {
                this.fillAccountUsingUserData();
            }
            this.step = 2;
            this.openExampleDialog(true);
            var t = this;
            setTimeout(function () { t.serviceName.nativeElement.focus() }, 100);
            this.refreshView();
            return;
        }
        if (this.utilityId != null && this.utilityId > 0) {
            this.account.utility = this.utilities[EntityBase.indexOf(this.utilities, new Utility(this.utilityId))];
        } else {
            this.account.utility = null!;
        }
        if (this.account.keyName == null || this.account.keyName == "") {
            this.addError("keyName");
        }
        if (this.account.utility == null) {
            this.addError("utilityId");
        }
        if (this.account.loadZone == null) {
            this.addError("loadZoneId");
        }
        if (this.account.rateClass == null) {
            this.addError("rateClassId");
        }
        if (this.account.serviceName == null || this.account.serviceName == "") {
            this.addError("serviceName");
        }
        if (this.account.addressLine1 == null || this.account.addressLine1 == "") {
            this.addError("addressLine1");
        }
        if (this.account.state == null) {
            this.addError("stateId");
        }
        if (this.account.zip == null || this.account.zip == "") {
            this.addError("zip");
        }
        if (this.account.city == null || this.account.city == "") {
            this.addError("city");
        }
        if (this.account.rateClass == null) {
            this.addError("rateClassId");
        }
        if (this.currentAccountNumber.value == null || this.currentAccountNumber.value.length == 0) {
            this.addError("accountNumber");
        }
        if (this.account.company != null && this.account.company.taxExempt == true && (this.account.company.federalTaxID == null || this.account.company.federalTaxID.length == 0)) {
            this.addError("FederalTaxID");
        }
        if (this.account.company != null && this.account.company.taxExempt == true && this.fileToUpload == null && this.account.company.taxExemptCertificateDocument == null) {
            this.addError("fileToUpload");
        }
        if (this.isCommercial() && (this.account.company == null || this.account.company.name == null || this.account.company.name.length == 0)) {
            this.addError("companyName");
        }
        if (this.errorY().showError()) {
            this.setErrorText("Please enter the data requested above in order to add your account.");
            return;
        }
        if (!this.validationService.validate("Account", "serviceName", this.account.serviceName, this.errorY)) {
            return;
        }
        if (!this.validationService.validate("Account", "addressLine1", this.account.addressLine1, this.errorY)) {
            return;
        }
        if (!this.validationService.validate("Account", "city", this.account.city, this.errorY)) {
            return;
        }
        if (!this.validationService.validate("Account", "zip", this.account.zip, this.errorY)) {
            return;
        }
        if (!this.validationService.validate("Account", "keyName", this.account.keyName, this.errorY)) {
            return;
        }
        if (!this.currentAccountNumber.valid) {
            this.addError("accountNumber");
            this.setErrorText("Account number is not correct.");
            return;
        }
        this.generalService.ifAccountNumberUsed(this.account.utility, this.currentAccountNumber.value, this.account.id).then(res => {
            if (res.value) {
                this.addError("accountNumber");
                this.setErrorText("Account with such number already exists.");
                this.refreshView();
                return;
            } else if (res.key) {
                this.addError("accountNumber");
                this.setErrorText("accountissue");
                this.refreshView();
                return;
            } else {
                this.account.accountNumber = this.currentAccountNumber.value;
                if (this.step == 2) {
                    var p1 = this.generalService.getUtilityCosts(this.account.utility!, this.account.rateClass!, this.account.loadZone!, this.account.state!, this.account.city!);
                    var p2 = this.generalService.getExistingContracts(null);

                    Promise.all([p1, p2]).then(x => {
                        this.utilityRate = x[0].utilityAvg;
                        this.communityRate = x[0].communityAvg;
                        this.existingContracts = [];
                        for (let i = 0; i < x[1].length; i++) {
                            if (x[1][i].status == ContractStatus.PENDING || x[1][i].status == ContractStatus.PENDING_NON_VERIFIED || x[1][i].status == ContractStatus.OPEN) {
                                this.existingContracts.push(x[1][i]);
                            }
                        }
                        if (this.existingContracts.length == 0) {
                            this.existingContractId = 0;
                        }
                        this.step = 3;
                        this.refreshView();
                    });
                    return;
                }
                this.account.startingContractConfirmed = true;
                if (this.setStartingContract == null) {
                    this.setErrorText("Confirm Supply Service Provider.");
                    this.refreshView();
                    return;
                }
                if (this.setStartingContract == 1) {
                    this.account.isCommunity = true;
                } else if (this.setStartingContract == -1) {
                    if (!this.checkContractData()) {
                        return;
                    }
                }
                this.confirm();
            }
            this.refreshView();
        });
    }

    parseBill(): void {
        if (this.imageMode == 0 && this.billsToUpload == null) {
            this.addError("billToUpload");
            this.setErrorText("Please select a bill in order to add your account.");
            return;
        }
        if (this.imageMode == 2 && this.billsToUpload == null) {
            this.setErrorText("Please make a photo of your bill in order to add your account.");
            return;
        }

        this.loading = true;
        this.progress = true;
        this.refreshView();

        var t = this;
        this.progressWidth = 0;
        this.clearErrors();
        setTimeout(() => {
            t.progressWidth = 100;
            t.progress = false;
            t.cdr.markForCheck();
        }, environment.BILL_PARSE_LENGTH);
        this.disableSelectorOfUtilityData = false;
        this.generalService.parseBill(this.billsToUpload).subscribe(templateAccountData => {
            this.loading = false;
            var templateAccount = templateAccountData.key;
            this.consumptions = templateAccountData.value;
            this.account.utility = templateAccount.utility;
            this.account.loadZone = templateAccount.loadZone;
            this.account.rateClass = templateAccount.rateClass;
            this.account.company = templateAccount.company;
            this.account.serviceName = templateAccount.serviceName;
            this.account.addressLine1 = templateAccount.addressLine1;
            this.account.addressLine2 = templateAccount.addressLine2;
            this.account.state = templateAccount.state;
            this.account.city = templateAccount.city;
            this.account.zip = templateAccount.zip;
            this.currentAccountNumber.setValue(templateAccount.accountNumber);
            this.account.keyName = templateAccount.keyName;
            this.account.billImages = templateAccount.billImages;
            if (this.account.utility == null) {
                this.setErrorText("The utility bill uploaded is not recognized as a Rhode Island Energy account. Please upload a qualifying Rhode Island Energy bill.");
                this.billsToUpload = [];
                this.refreshView();
                return;
            }
            let foundRateClass = false;
            for (let i = 0; i < this.rateClasses.length; i++) {
                if (this.account.rateClass != null && this.account.rateClass.id == this.rateClasses[i].id) {
                    foundRateClass = true;
                    break;
                }
            }
            if (this.account.utility != null && this.account.loadZone != null && this.account.rateClass != null) {
                this.disableSelectorOfUtilityData = true;
            }
            if (!foundRateClass && this.account.rateClass != null) {
                this.rateClasses.push(this.account.rateClass);
            }
            this.initCompanyRelatedData();
            this.step = 2;
            this.generalService.ifAccountNumberUsed(this.account.utility, this.currentAccountNumber.value, null).then(res => {
                if (res.value) {
                    this.addError("accountNumber");
                    this.setErrorText("Account with such number already exists.");
                } else if (res.key) {
                    this.addError("accountNumber");
                    this.setErrorText("accountissue");
                }
                this.refreshView();
            });
            this.loaderService.setDisabled(false);
            this.refreshView();
            return;
        });
    }

    initCompanyRelatedData(): void {
        firstValueFrom(this.generalService.getUsersCompanies()).then((companies: any) => {
            this.userCompanies = [];
            for (let i = 0; i < companies.length; i++) {
                if (companies[i] != null) {
                    this.userCompanies.push(companies[i]);
                }
            }
            if (this.userCompanies.length == 0) {
                this.newCompanyRegistration.set(true);
            }
            this.refreshView();
        });

        this.isCommercial.set(this.account.company != null || this.account.rateClass?.isCommercial!);
        this.taxExempt = false;
        if (this.isCommercial()) {
            this.changeCommercial();
        }
        this.selectExistingCompany();
        this.refreshView();
    }

    checkAutoGeneratedAccount(templateAccount: Account): void {
        if (!templateAccount.autoGenerated) {
            this.goto("/user/home");
            return;
        }
        this.preRegisteredAccountUpdateMode = true;
        this.account.utility = templateAccount.utility;
        this.account.loadZone = templateAccount.loadZone;
        this.account.rateClass = templateAccount.rateClass;
        this.account.company = templateAccount.company;
        this.account.serviceName = templateAccount.serviceName;
        this.account.addressLine1 = templateAccount.addressLine1;
        this.account.addressLine2 = templateAccount.addressLine2;
        this.account.state = templateAccount.state;
        this.account.city = templateAccount.city;
        this.account.zip = templateAccount.zip;
        this.currentAccountNumber.setValue(templateAccount.accountNumber);
        this.account.keyName = templateAccount.keyName;
        this.account.id = templateAccount.id;
        let foundRateClass = false;
        for (let i = 0; i < this.rateClasses.length; i++) {
            if (this.account.rateClass != null && this.account.rateClass.id == this.rateClasses[i].id) {
                foundRateClass = true;
                break;
            }
        }
        if (!foundRateClass && this.account.rateClass != null) {
            this.rateClasses.push(this.account.rateClass);
        }
        if (this.account.rateClass?.isCommercial || this.account.company!=null) {
            this.isCommercial.set(true);
        }
        this.step = 2;
        this.refreshView();
    }

    checkContractData(): boolean {
        this.clearErrors();
        if (this.existingContractId != null && this.existingContractId > 0) {
            return true;
        }
        if (this.existingContract.supplierName == null || this.existingContract.supplierName.length == 0) {
            this.addError("currentSupplierName");
        }
        if (
            this.existingContract.price == null || this.existingContract.price == 0
        ) {
            this.addError("currentSupplierPrice");
        }
        if (this.termFrom.value == null || this.termFrom.value.length == 0) {
            this.addError("termFrom");
        }
        if (this.termTo.value == null || this.termTo.value.length == 0) {
            this.addError("termTo");
        }
        if (this.errorY().showError()) {
            this.setErrorText("Please enter the data requested above in order to add your account.");
            return false;
        }
        if (this.termFrom.value >= this.termTo.value) {
            this.addError("termFrom");
            this.addError("termTo");
            this.setErrorText("The ending price plan date shall be ahead of the starting price plan date.");
            return false;
        }
        if (!this.validationService.validate("ExistingContract", "supplierName", this.existingContract.supplierName, this.errorY)) {
            return false;
        }
        this.existingContract.termFrom = this.termFrom.value.toJSDate();
        this.existingContract.termTo = this.termTo.value.toJSDate();
        return true;
    }

    confirm(): void {
        this.clearErrors();
        if (this.preRegisteredAccountUpdateMode) {
            this.generalService.updateAccount(this.account, [], [], this.fileToUpload).subscribe({
                next: (res: any) => {
                    this.generalService.clearUserCompaniesFromSession();
                    this.finish(false);
                },
                error: (err: any) => {
                    if (err.status == 400) {
                        this.setErrorText(err.error);
                        this.refreshView();
                    }
                }
            });
        } else {
            this.generalService.addAccount(this.account, this.existingContractId, (this.setStartingContract == -1 && this.existingContractId == -1) ? this.existingContract : null, this.consumptions, [], this.fileToUpload).subscribe({
                next: (res: number | null) => {
                    this.generalService.clearUserCompaniesFromSession();
                    if (res != null && res > 0) {
                        this.account.id = res;
                        this.finish(false);
                    }
                },
                error: (err: any) => {
                    if (err.status == 400) {
                        this.setErrorText(err.error);
                        this.refreshView();
                    }
                }
            });
        }
    }

    gotoStep(num: number): void {
        this.step = num;
        this.clearErrors();
        this.refreshView();
    }

    finish(withPopup: boolean): void {
        var t = this;
        if (!withPopup) {
            t.generalService.switchStoredAccount(this.account);
            t.goto("/user/home");
            return;
        }
        this.openResultsPopup("Your new account has been created", "This account is subscribed to Nonstop Shop ai Level 1. You can modify or deactivate your subscription at any time by going to Main Menu > AI Tasks.", () => {
            t.generalService.switchStoredAccount(this.account);
            t.goto("/user/home");
        });
    }

    reloadSelects(): void {
        if (this.utilityId != null && this.utilityId > 0) {
            let temp = this.account;
            let currentUtility = this.utilities[EntityBase.indexOf(this.utilities, new Utility(this.utilityId))];
            this.account.utility = currentUtility;
            this.currentAccountNumber.setValue(this.account.accountNumber);
            if (temp != null && currentUtility != null) {
                this.states = [];
                if (currentUtility.state != null) {
                    this.states.push(currentUtility.state);
                    this.account.state = currentUtility.state;
                }
                this.generalService.getRateClasses(currentUtility).then((rateClasses: any) => {
                    this.rateClasses = [];
                    for (let i = 0; i < rateClasses.length; i++) {
                        this.rateClasses.push(rateClasses[i]);
                    }
                    if (this.rateClasses.length == 1) {
                        this.account.rateClass = this.rateClasses[0];
                    }
                    for (let i = 0; i < this.rateClasses.length; i++) {
                        var isResidential = true;
                        if (this.toBeUsedUser.company != null) {
                            isResidential = false;
                        }
                        if (isResidential && !this.rateClasses[i].isCommercial && currentUtility != null && currentUtility.defaultRateClassForResidential != null && currentUtility.defaultRateClassForResidential.id == this.rateClasses[i].id
                            ||
                            !isResidential && this.rateClasses[i].isCommercial && currentUtility != null && currentUtility.defaultRateClassForCommercial != null && currentUtility.defaultRateClassForCommercial.id == this.rateClasses[i].id
                        ) {
                            this.account.rateClass = this.rateClasses[i];
                        }
                    }
                    this.refreshView();
                });
                this.generalService.getLoadZones(currentUtility).then((loadZones: any) => {
                    this.loadZones = loadZones;
                    if (this.loadZones.length == 1) {
                        this.account.loadZone = this.loadZones[0];
                    }
                    for (let i = 0; i < this.loadZones.length; i++) {
                        if (currentUtility.defaultLoadZone != null && currentUtility.defaultLoadZone.id == this.loadZones[i].id) {
                            this.account.loadZone = this.loadZones[i];
                        }
                    }
                    this.refreshView();
                });
                this.filterCities(currentUtility.state);
            }
        }
    }

    handleFileInput(event: Event) {
        this.loaderService.setDisabled(true);
        const target = event.target as HTMLInputElement;
        if (this.step == 1) {
            this.billsToUpload = [];
            this.billsToUpload.push((target.files as FileList).item(0) as File);
            this.resetError("billToUpload");
            this.nextStep();
            return;
        }
        this.fileToUpload = (target.files as FileList).item(0) as File;
        this.resetError("fileToUpload");
        this.refreshView()
    }

    deleteBill(pos: number) {
        this.billsToUpload.splice(pos, 1);
        this.refreshView()
    }

    deleteFile() {
        this.fileToUpload = null;
        this.refreshView()
    }

    downloadFile(ifExisting: boolean, id: number | null) {
        if (!ifExisting) {
            this.generalService.sendFileToClient(this.fileToUpload);
        } else if (id != null) {
            this.generalService.openDocument(id);
        }
    }

    downloadBill(pos: number) {
        this.generalService.sendFileToClient(this.billsToUpload[pos]);
    }

    deleteExistingFile(key: number) {
        this.deleteTaxExemptCertificateDocument = true;
        if (this.toBeUsedUser.company != null) {
            this.toBeUsedUser.company.taxExemptCertificateDocument = null!;
        }
        this.refreshView()
    }

    goBack(): void {
        this.router.navigate(["/user/home"]);
    }

    filterCities(state: State): void {
        this.cities = [];
        if (state == null) {
            return;
        }
        this.generalService.getCities(state).then((cities) => {
            this.cities = cities;
            this.refreshView()
        });
    }

    openExampleDialog(firstTime: boolean): void {
        if (firstTime && !this.defaultShowAccountInfo) {
            return;
        }
        this.defaultShowAccountInfo = false;
        let dialogRef = this.dialog.open(AccountExampleDialog, {
            disableClose: false
            , panelClass: "example-popup"
        });
        var t = this;
        dialogRef.afterClosed().subscribe(result => {
            t.serviceName.nativeElement.focus();
        });
    }

    getAccountIdent(a: Account): string {
        return Account.accountIdent(a);
    }

    inactiveDatesFilter(d: DateTime | null): boolean {
        if (d == null || this.globalStart == null || this.globalEnd == null) {
            return false;
        }
        return (d > this.globalStart) && (d < this.globalEnd);
    }

    inactiveDatesFilterTermTo(d: DateTime | null): boolean {
        if (d == null) {
            return false;
        }
        let from;
        if (this.termFrom == null || this.termFrom.value == null) {
            from = this.globalStart;
        } else {
            from = this.termFrom.value;
        }
        return (d > from) && d < this.globalEnd;
    }

    canCapture(): boolean {
        return (navigator.mediaDevices != null);
    }

    reportAccountIssue(): void {
        var dialogRef = this.dialog.open(AccountNumberIssueDialog, {
            disableClose: false,
            panelClass: "i-icon-popup"
        });
        dialogRef.componentInstance.info = "Please, provide more details about your account (optional).";
        dialogRef.componentInstance.message = "My Account Number: " + this.currentAccountNumber.value + " has been used without my authorization. Please, investigate this.";
        dialogRef.componentInstance.utility = this.account.utility;
        dialogRef.componentInstance.accountNumber = this.currentAccountNumber.value;

        dialogRef.afterClosed().subscribe((result: any) => {
            dialogRef = null!;
        });
    }

    taxExemptChanged(): void {
        if (this.newCompanyRegistration() || this.taxExemptAllowToAdd()) {
            this.account!.company!.taxExempt = this.taxExempt;
            return;
        }
        if (!this.taxExempt) {
            this.account.company = new Company();
            this.account.company.name = this.toBeUsedUser.company!.name;
            this.account.company.taxExempt = false;
        } else {
            this.account.company = this.toBeUsedUser.company;
            this.account.company!.taxExempt = true;
        }
    }

    setNewCompany(): void {
        this.taxExempt = false;
        this.account.company = new Company();
        this.taxExempt = false;
        this.clearErrors();
        this.newCompanyRegistration.set(true);
    }

    selectExistingCompany(): void {
        this.taxExempt = this.account.company?.taxExempt!;
        this.taxExemptAllowToAdd.set(true);
        this.clearErrors();
        this.refreshView();
    }

    changeCommercial(): void {
        if (this.account.rateClass?.isCommercial) {
            this.isCommercial.set(true);
            if (this.account.company == null) {
                this.account.company = new Company();
            }
            return;
        }
        this.clearErrors();
        this.isCommercial.update(v => !v);
    }

    switchToCompanySelect(): void {
        this.account.company = null;
        this.newCompanyRegistration.set(false);
        this.clearErrors();
    }


    boundInactiveDatesFilter = this.inactiveDatesFilter.bind(this);
    boundInactiveDatesFilterTermTo = this.inactiveDatesFilterTermTo.bind(this);
}
